import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/api/client';

// Verified dashboard SPA settings. These public identifiers deliberately do not
// inherit local Vite environment overrides or the native application's client.
const AUTH0_DOMAIN = 'dev-yhomrga8ggwbroi7.us.auth0.com';
const AUTH0_CLIENT_ID = '9y8aGuuWQnfa5KSsT9bk4pJH9WE380Qf';
const AUTH0_AUDIENCE = 'https://obdaiazurefunctions20240928071526-uat.azurewebsites.net/api';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <AuthInitializer>{children}</AuthInitializer>
    </Auth0Provider>
  );
}

// Auth0 error codes that mean "this browser's cached session is dead; a fresh login is required".
const LOGIN_REQUIRED_ERRORS = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'invalid_grant',
]);

const REAUTH_GUARD_KEY = 'obdai.reauth.at';

// One re-auth per page load. A single failed request reaches reauth() twice (the token getter
// below, then the request interceptor's catch), and a page fires several requests in parallel, so
// without this the second call landed inside the 30 s loop guard and signed the user out instead
// of signing them back in. The loop guard is only meant to compare across page loads.
let reauthStarted = false;

// Component to initialize auth state and set up API client
function AuthInitializer({ children }: { children: ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect, logout } = useAuth0();

  useEffect(() => {
    // One re-auth per 30 s. If a login round-trip lands us straight back here (stale local cache
    // that Auth0 keeps rejecting), clear the cache with a logout instead of looping (#164).
    const reauth = (reason: string) => {
      if (reauthStarted) return;
      reauthStarted = true;
      const last = Number(sessionStorage.getItem(REAUTH_GUARD_KEY) || 0);
      const now = Date.now();
      if (now - last < 30_000) {
        console.warn(`Auth re-run (${reason}) looped within 30 s; clearing the cached session.`);
        sessionStorage.removeItem(REAUTH_GUARD_KEY);
        logout({ logoutParams: { returnTo: window.location.origin } });
        return;
      }
      sessionStorage.setItem(REAUTH_GUARD_KEY, String(now));
      loginWithRedirect({ appState: { returnTo: window.location.pathname + window.location.search } });
    };

    apiClient.setAuthRequiredHandler(reauth);

    if (isAuthenticated) {
      // Set up the API client to use Auth0 tokens
      apiClient.setTokenGetter(async () => {
        try {
          return await getAccessTokenSilently();
        } catch (err) {
          const code = (err as { error?: string })?.error ?? '';
          if (LOGIN_REQUIRED_ERRORS.has(code)) {
            reauth(code);
          }
          throw err;
        }
      });
    }
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect, logout]);

  return <>{children}</>;
}

// Custom hook for auth with navigation
export function useAuthWithNavigation() {
  const auth = useAuth0();
  const navigate = useNavigate();

  const loginWithRedirect = () => {
    auth.loginWithRedirect({
      appState: { returnTo: window.location.pathname },
    });
  };

  const handleLogout = () => {
    auth.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  return {
    ...auth,
    loginWithRedirect,
    handleLogout,
    navigate,
  };
}

// Callback handler component
export function AuthCallback() {
  const { handleRedirectCallback, isLoading, error } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const result = await handleRedirectCallback();
        const returnTo = result?.appState?.returnTo || '/';
        navigate(returnTo);
      } catch (err) {
        console.error('Auth callback error:', err);
        navigate('/');
      }
    };

    if (!isLoading && !error) {
      handleCallback();
    }
  }, [handleRedirectCallback, isLoading, error, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-obdai-deep">
        <div className="text-center text-white">
          <h2 className="text-xl font-bold mb-2">Authentication Error</h2>
          <p className="text-gray-400">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-obdai-deep">
      <div className="text-center text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-obdai-gradient-start mx-auto mb-4"></div>
        <p>Completing login...</p>
      </div>
    </div>
  );
}
